import { Link, useLocation } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import Icon, { type IconName } from "@/src/lib/components/custom/Icon";
import { Badge } from "@/src/lib/components/ui/badge";
import { Button } from "@/src/lib/components/ui/button";
import { useApi } from "@/src/lib/hooks/api/use-api";

interface INavigationItem {
	icon: IconName;
	path: string;
	labelKey: string;
	badge?: number;
}

export default function AdminSidebar() {
	const { pathname } = useLocation();
	const { analytics } = useApi();
	const { t } = useTranslation();

	// Fetch analytics data for badges
	const { data: analyticsData } = analytics.getDashboard();

	// Admin stats from analytics
	const adminStats = {
		pendingOrders: analyticsData?.today.pendingOrders || undefined,
	};

	const navigationItems: INavigationItem[] = [
		{
			icon: "LayoutDashboard",
			path: "/admin",
			labelKey: "admin.sidebar.dashboard",
			badge: adminStats.pendingOrders,
		},
		{
			icon: "Package",
			path: "/admin/orders",
			labelKey: "admin.sidebar.orders",
			badge: adminStats.pendingOrders,
		},
		{
			icon: "UtensilsCrossed",
			path: "/admin/menu",
			labelKey: "admin.sidebar.menu",
		},
		{ icon: "Users", path: "/admin/users", labelKey: "admin.sidebar.users" },
		{ icon: "Shield", path: "/admin/admins", labelKey: "admin.sidebar.admins" },
	];

	return (
		<div className="fixed top-[var(--navbar-height)] h-[calc(100dvh-var(--navbar-height))] w-48 left-0 z-40 bg-background hidden lg:flex">
			<div className="flex flex-col w-full gap-2 px-3 mt-6">
				{navigationItems.map((item) => {
					const isActive = pathname === item.path;
					return (
						<Button
							key={item.path}
							variant={isActive ? "primary" : "ghost"}
							size="sm"
							className={`w-full justify-start gap-3 px-3 py-2 h-auto`}
							asChild
						>
							<Link to={item.path}>
								<Icon name={item.icon} className="size-4 shrink-0" />
								<span className="font-medium truncate">{t(item.labelKey)}</span>
								{item.badge && item.badge > 0 && (
									<Badge className="ml-auto text-xs px-1.5 py-0.5 h-4 min-w-4">
										{item.badge > 9 ? "9+" : item.badge}
									</Badge>
								)}
							</Link>
						</Button>
					);
				})}
			</div>
		</div>
	);
}
